// FILE: src/utils/firebase/features/system/writers.js
// Purpose: System-scoped writers for administrative actions (e.g., nuking user data)

import { getDbOrThrow } from "../../core/firestoreCore";
import { COLLECTIONS } from "../../core/collections";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  writeBatch,
} from "firebase/firestore";

/** Delete all docs owned by user from a user_* collection. Returns number deleted. */
async function deleteAllForUserFromCollection({ user, collectionKey }) {
  const db = getDbOrThrow();
  const key = String(collectionKey);
  const collName = COLLECTIONS[key];
  if (!user?.uid) throw new Error("Not signed in.");
  if (!collName) throw new Error(`Unknown collection key: ${key}`);

  // Special-case: userTransactionsMeta is a single per-user doc keyed by email or uid
  if (key === "userTransactionsMeta") {
    const emailId = String(user.email || "").toLowerCase();
    const uidId = String(user.uid);
    let deleted = 0;
    if (emailId) {
      const r = doc(collection(db, collName), emailId);
      const s = await getDoc(r);
      if (s.exists()) {
        const b = writeBatch(db);
        b.delete(r);
        await b.commit();
        deleted += 1;
      }
    }
    if (uidId) {
      const r = doc(collection(db, collName), uidId);
      const s = await getDoc(r);
      if (s.exists()) {
        const b = writeBatch(db);
        b.delete(r);
        await b.commit();
        deleted += 1;
      }
    }
    return deleted;
  }

  // Generic path: delete in batches by userId equality
  let total = 0;
  const PAGE = 300; // under 500 write limit per batch
  // Loop until no results
  // Firestore does not support offset for equality; we repeatedly fetch and delete
  // until empty, since deletes remove them from subsequent queries.
  for (;;) {
    const q = query(collection(db, collName), where("userId", "==", user.uid), limit(PAGE));
    const snap = await getDocs(q);
    if (snap.empty) break;
    const b = writeBatch(db);
    let n = 0;
    snap.forEach((d) => {
      b.delete(doc(collection(db, collName), d.id));
      n++;
    });
    await b.commit();
    total += n;
    if (n < PAGE) break; // last page
  }
  return total;
}

/**
 * Nuke selected user collections. Returns an array of { key, name, deleted }.
 */
export async function nukeUserCollections({ user, collectionKeys = [], status }) {
  if (!user?.uid) throw new Error("Not signed in.");
  const results = [];
  for (const key of collectionKeys) {
    const name = COLLECTIONS[key];
    if (!name) continue;
    status?.(`Deleting from ${name} ...`);
    const deleted = await deleteAllForUserFromCollection({ user, collectionKey: key });
    results.push({ key, name, deleted });
  }
  status?.("Done");
  return results;
}


